import { rulesFor } from '@words-happen/board-rules-engine-client';
import type { BoardGamePlacedTile, BoardGameTile, BoardGameView } from './board-game-types';

// Sample games for the board game showcases and specs: rules version 1, the classic board.

const rules = rulesFor(1);

const tile = (id: number, face: string): BoardGameTile => ({ id, face, points: rules.facePoints(face) });

const across = (
  id: number,
  face: string,
  row: number,
  col: number,
  turn: number,
  seat: number,
): BoardGamePlacedTile => ({
  ...tile(id, face),
  row,
  col,
  dir: 'h',
  turn,
  seat,
});

const down = (id: number, face: string, row: number, col: number, turn: number, seat: number): BoardGamePlacedTile => ({
  ...across(id, face, row, col, turn, seat),
  dir: 'v',
});

const START = { row: 5, col: 5 };

/**
 * Two turns in: Sam (seat 1, the player) opened with CART through the start square, and Priya (seat 2) played TIN
 * down from its T. The player's turn.
 */
export const SHOWCASE_BOARD_GAME: BoardGameView = {
  id: 'g1',
  status: 'active',
  endReason: null,
  rulesVersion: 1,
  ruleChecks: true,
  shape: 'classic',
  size: rules.BOARD_SIZE,
  diagonals: false,
  swapMode: 'then-play',
  swapLimit: 4,
  start: START,
  bonuses: rules
    .classicPremiums('classic', START)
    .filter(({ row, col }) => row !== START.row || col !== START.col)
    .map(({ row, col, kind }) => ({ row, col, kind })),
  turnNumber: 2,
  isMyTurn: true,
  players: [
    { seat: 1, isMe: true, name: 'Sam', score: 18, result: null },
    { seat: 2, isMe: false, name: 'Priya', score: 6, result: null },
  ],
  board: [
    across(10, 'C', 5, 3, 1, 1),
    across(11, 'A', 5, 4, 1, 1),
    across(12, 'R', 5, 5, 1, 1),
    across(13, 'T', 5, 6, 1, 1),
    down(20, 'IN', 6, 6, 2, 2),
  ],
  rack: [tile(1, 'S'), tile(2, 'E'), tile(3, 'D'), tile(4, 'ER'), tile(5, 'O'), tile(6, 'L'), tile(7, 'P')],
  bagCount: 61,
  turns: [
    { number: 1, seat: 1, kind: 'play', words: [{ word: 'CART', points: 18 }], points: 18, swappedCount: 0 },
    { number: 2, seat: 2, kind: 'play', words: [{ word: 'TIN', points: 6 }], points: 6, swappedCount: 0 },
  ],
};

/** The same game on Priya's turn. */
export const SHOWCASE_THEIR_TURN: BoardGameView = { ...SHOWCASE_BOARD_GAME, isMyTurn: false };

/** The game played out: Priya played her last tile, and Sam won. */
export const SHOWCASE_FINISHED_GAME: BoardGameView = {
  ...SHOWCASE_BOARD_GAME,
  status: 'finished',
  endReason: 'went-out',
  isMyTurn: false,
  turnNumber: 3,
  players: [
    { seat: 1, isMe: true, name: 'Sam', score: 312, result: 'won' },
    { seat: 2, isMe: false, name: 'Priya', score: 280, result: 'lost' },
  ],
  bagCount: 0,
  turns: [
    ...SHOWCASE_BOARD_GAME.turns,
    { number: 3, seat: 2, kind: 'play', words: [{ word: 'TINS', points: 6 }], points: 6, swappedCount: 0 },
  ],
};

/** A word the sample games' preview accepts: a small list, enough for the sample board. */
const SHOWCASE_WORDS = new Set(['CART', 'CARTS', 'TIN', 'TINS', 'SO', 'DO', 'LO', 'PO', 'RED', 'ROD', 'ODE', 'LED']);

export const showcaseIsWord = (word: string): boolean => SHOWCASE_WORDS.has(word.toUpperCase());
